import { useEffect, useState, useRef } from "react";
import RightModal from "../components/templates/RightModal";
import { getSuppliersList, selectSuppliersData, selectSuppliersLoading } from "../redux/suppliers/suppliersSlice";
import DynamicIcon from "../components/DynamicIcon";
import LoadingSection from "../components/LoadingSection";
import { selectAccountData } from "../redux/main/accountSlice";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";
import DynamicTitle from "../components/DynamicTitle";
import { getNodeApiUrl } from "../config/api.config";
import { useColorPalette } from '../hooks/useColorPalette';
import ListPagination from "../components/templates/ListPagination";
import SupplierLine from "../components/suppliersPage/SupplierLine";
import AddEditSupplier from "../components/suppliersPage/AddEditSupplier";
import ViewSupplier from "../components/suppliersPage/ViewSupplier";
import { getSupplierDetails, resetSupplierDetails } from "../redux/suppliers/suppliersSlice";

export default function SuppliersList() {
    const dispatch = useDispatch();
    const palette = useColorPalette();
    const suppliersList = useSelector(selectSuppliersData);
    const suppliersLoading = useSelector(selectSuppliersLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const [searchTerm, setSearchTerm] = useState('');
    const [dataTableKey, setDataTableKey] = useState(Date.now());
    const tableRef = useRef(null);
    const [hasBrandsFilter, setHasBrandsFilter] = useState('');
    
    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalSupplierId = useSelector(selectRightModalId);
    
    // Funzione che verrà passata al componente ListPagination
    const handlePageUpdate = (start, length) => {
        dispatch(getSuppliersList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 3 ? searchTerm : '',
            start,
            length,
            hasBrands: hasBrandsFilter
        }));
        dispatch(resetRefreshNow());
    };

    // Eseguo la chiamata iniziale quando il componente viene montato o quando cambiano i filtri
    useEffect(() => {
        if (accountData?._id) {
            dispatch(getSuppliersList({
                accountId: accountData._id,
                token,
                search: searchTerm.length >= 3 ? searchTerm : '',
                start: 0, // Inizio da zero
                length: 10, // Uso la dimensione della pagina predefinita
                hasBrands: hasBrandsFilter
            }));
            dispatch(resetRefreshNow());
            
            // Aggiorna la DataTable quando cambiano i filtri
            setDataTableKey(Date.now());
        }
    }, [dispatch, accountData, token, searchTerm, hasBrandsFilter]);
    
    // Gestione degli eventi per i pulsanti nella tabella
    useEffect(() => {
        const tableNode = tableRef.current;
        if (!tableNode) return;

        const handleActionClick = (e) => {
            const target = e.target.closest('[action]');
            if (!target) return;

            const supplierId = target.getAttribute('data-id');
            const action = target.getAttribute('action');
            
            if (action === 'view') {
                dispatch(setModal({
                    isOpen: true,
                    mode: 'view',
                    id: supplierId
                }));
            } else if (action === 'edit') {
                dispatch(setModal({
                    isOpen: true,
                    mode: 'edit',
                    id: supplierId
                }));
            }
        };

        tableNode.addEventListener('click', handleActionClick);
        return () => {
            tableNode.removeEventListener('click', handleActionClick);
        };
    }, [dispatch]);
    
    // Carica i dettagli del fornitore quando il modale viene aperto per modifica o visualizzazione
    useEffect(() => {
        if (rightModalIsOpen && rightModalSupplierId && (rightModalMode === 'edit' || rightModalMode === 'view')) {
            dispatch(getSupplierDetails({
                supplierId: rightModalSupplierId,
                token
            }));
        }
    }, [rightModalIsOpen, rightModalSupplierId, rightModalMode, dispatch, token]);

    // Reset dei dettagli fornitore quando si chiude il modale
    useEffect(() => {
        if (!rightModalIsOpen) {
            dispatch(resetSupplierDetails());
        }
    }, [rightModalIsOpen, dispatch]);

    // Aggiorna la tabella quando il modal viene chiuso
    useEffect(() => {
        if (!rightModalIsOpen && dataTableKey) {
            // Aggiorna la tabella
            setDataTableKey(Date.now());
        }
    }, [rightModalIsOpen]);
    
    // Avvio il refresh dei dati quando isRefreshNow della RightModal si attiva
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow) handlePageUpdate(0, 10);
    }, [rightModalIsRefreshNow]);
    
    return (
        <div className="page">
            {suppliersLoading && <LoadingSection isFixed />}
            <DynamicTitle title="Fornitori" />
            
            <h1>Fornitori</h1>
            <div className="box">
                {/* Barra di ricerca e filtri */}
                <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                    <div className="relative w-96">
                        <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                            <DynamicIcon iconClass="fa-solid fa-search" />
                        </div>
                        <input 
                            type="text" 
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full ps-10 p-2.5" 
                            placeholder="Cerca fornitore..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button 
                                className="absolute inset-y-0 end-0 flex items-center pe-3"
                                onClick={() => setSearchTerm('')}
                            >
                                <DynamicIcon iconClass="fa-solid fa-times" />
                            </button>
                        )}
                    </div>
                    
                    {/* Filtri */}
                    <div className="flex gap-2">
                        {/* Filtro per Marchi */}
                        <select 
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5"
                            value={hasBrandsFilter}
                            onChange={(e) => setHasBrandsFilter(e.target.value)}
                        >
                            {[{value: "", label: "Tutti i fornitori"}, 
                              {value: "true", label: "Con marchi"}, 
                              {value: "false", label: "Senza marchi"}].map((option, index) => (
                                <option key={`brand-filter-${index}`} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                        
                        {/* Bottone Nuovo Fornitore */}
                        <button 
                            className="primary" 
                            onClick={() => dispatch(setModal({ mode: 'add' }))}
                        >
                            <DynamicIcon iconClass="fa-solid fa-plus" />
                            Nuovo Fornitore
                        </button>
                    </div>
                </div>

                {/* Lista fornitori */}
                <div className="w-full my-4" ref={tableRef}>
                    {suppliersLoading || !accountData?._id ? (
                        <LoadingSection />
                    ) : (
                        <ListPagination
                            initTotalElements={suppliersList?.recordsFiltered || 0}
                            onPageChange={handlePageUpdate}
                        >
                            {/* Tabella fornitori */}
                            <div className="w-full text-sm flex flex-col gap-2">
                                {/* Headers */}
                                <div className="w-full flex justify-between font-bold rounded-t-lg" style={{ backgroundColor: palette.tableHeaderBg, color: palette.tableHeaderText }}>
                                    <div className="px-6 py-4 w-1/5">NOME</div>
                                    <div className="px-6 py-4 w-1/5">REFERENTE</div>
                                    <div className="px-6 py-4 w-1/5">EMAIL</div>
                                    <div className="px-6 py-4 w-1/5">TELEFONO</div>
                                    <div className="px-6 py-4 w-1/5 flex justify-end">AZIONI</div>
                                </div>
                                
                                {/* Rows */}
                                {
                                    suppliersList?.data?.map((supplier, index) => {
                                        const isEven = index % 2 === 0;
                                        const rowBgColor = isEven ? palette.tableRowLight : palette.tableRowDark;
                                        const rowTextColor = isEven ? palette.textOnTableRowLight : palette.textOnTableRowDark;

                                        return (
                                            <SupplierLine
                                                key={supplier._id}
                                                supplierData={supplier}
                                                index={index}
                                                rowBgColor={rowBgColor}
                                                rowTextColor={rowTextColor}
                                                rowHoverBgColor={palette.highlightBg}
                                                rowHoverTextColor={palette.textOnHighlight}
                                                onDelete={() => handlePageUpdate(0, 10)}
                                            />
                                        );
                                    })
                                }
                                
                                {/* Messaggio quando non ci sono dati */}
                                {(!suppliersList?.data || suppliersList.data.length === 0) && !suppliersLoading && (
                                    <div className="w-full py-4 text-center text-gray-500">
                                        Nessun fornitore trovato
                                    </div>
                                )}
                            </div>
                        </ListPagination>
                    )}
                </div>
            </div>

            {/* RIGHT MODAL per inserimento o modifica fornitore */}
            {
                rightModalIsOpen && (
                    <RightModal>
                        {(rightModalMode === 'add' || rightModalMode === 'edit') && (
                            <AddEditSupplier mode={rightModalMode} />
                        )}
                        {
                            rightModalMode === 'view' && rightModalSupplierId && (
                                <ViewSupplier />
                            )
                        }
                    </RightModal>
                )
            }
        </div>
    );
}
